import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { Spinner } from '../../ui/Spinner/Spinner';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import type { EntityExtractionStatus } from '../../types/acombaBridge';
import type { ExtractionProgressProps } from './types';

const ENTITIES = ['customers', 'invoices', 'payments'] as const;
type Entity = (typeof ENTITIES)[number];

const entityLabelKey: Record<Entity, string> = {
    customers: TranslationKey.AcombaBridge.Extraction.Customers,
    invoices: TranslationKey.AcombaBridge.Extraction.Invoices,
    payments: TranslationKey.AcombaBridge.Extraction.Payments,
};

function EntityStatusIndicator({ status }: { status: EntityExtractionStatus }) {
    const { t } = useTranslation();

    if (status === 'fetching') {
        return <Spinner className="size-text" testId="extraction-entity-spinner" />;
    }
    if (status === 'done') {
        return (
            <span className="text-success-100 font-normal" aria-label={t(TranslationKey.AcombaBridge.Extraction.Done)}>
                ✓
            </span>
        );
    }
    if (status === 'failed') {
        return (
            <span className="text-danger-100 font-normal" aria-label={t(TranslationKey.AcombaBridge.Extraction.Failed)}>
                ✗
            </span>
        );
    }
    if (status === 'cancelled') {
        return (
            <span className="text-primary-500" aria-label={t(TranslationKey.AcombaBridge.Extraction.Cancelled)}>
                —
            </span>
        );
    }
    // idle: not started yet — labelled for screen-reader parity with the other states.
    return (
        <span className="text-primary-400" aria-label={t(TranslationKey.AcombaBridge.Extraction.Idle)}>
            ·
        </span>
    );
}

function entityStatusLabel(status: EntityExtractionStatus, t: (key: string) => string): string {
    switch (status) {
        case 'fetching': return t(TranslationKey.AcombaBridge.Extraction.Fetching);
        case 'done': return t(TranslationKey.AcombaBridge.Extraction.Done);
        case 'failed': return t(TranslationKey.AcombaBridge.Extraction.Failed);
        case 'cancelled': return t(TranslationKey.AcombaBridge.Extraction.Cancelled);
        default: return '';
    }
}

/**
 * Compact extraction progress block shown before the backend sync phases begin.
 * Renders one row per entity (Customers / Invoices / Payments) with its current state.
 * Only rendered while extraction is active or has partially failed — the parent controls visibility.
 */
export const ExtractionProgress: React.FC<ExtractionProgressProps> = ({ extractionStatus, onRetry }) => {
    const { t } = useTranslation();

    return (
        <div data-testid="extraction-progress" className="flex flex-col gap-s w-full">
            <p className="text-primary-700 text-s font-normal">
                {t(TranslationKey.AcombaBridge.Extraction.Heading)}
            </p>
            <div className="flex flex-col gap-xs">
                {ENTITIES.map((entity) => {
                    const status = extractionStatus[entity];
                    const label = entityStatusLabel(status, t);
                    return (
                        <div
                            key={entity}
                            data-testid={`extraction-entity-${entity}`}
                            className="flex items-center gap-s text-s"
                        >
                            <span className="w-4 flex items-center justify-center">
                                <EntityStatusIndicator status={status} />
                            </span>
                            <span className="text-primary-1000 w-24">{t(entityLabelKey[entity])}</span>
                            {label && (
                                <span className={status === 'failed' ? 'text-danger' : status === 'done' ? 'text-success' : 'text-primary-600'}>
                                    {label}
                                </span>
                            )}
                            {status === 'failed' && onRetry && (
                                <LinkButton
                                    size="xs"
                                    data-testid={`extraction-retry-${entity}`}
                                    onClick={() => onRetry(entity)}
                                    className="ml-auto focus:outline-none"
                                >
                                    {t(TranslationKey.AcombaBridge.Extraction.RetryEntity)}
                                </LinkButton>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
